export const NAV_LABELS = {
  protectionCoverage: 'Protection Coverage',
  securityActivity: 'Security Activity',
  evidenceExport: 'Evidence Export',
  adminAccessRules: 'Admin Access Location Rules',
};

export const STATUS_LABELS = {
  covered: 'Covered',
  managed: 'Managed by ShadowScan',
  needsHost: 'External action',
  actionRequired: 'Action required',
  monitoringPlanned: 'Monitoring planned',
};

export const STATUS_DESCRIPTIONS = {
  covered: 'Protections are active and monitoring is healthy.',
  managed: 'ShadowScan can complete this change in-platform.',
  needsHost: 'Requires changes by your host or developer (outside ShadowScan).',
  actionRequired: 'Action is required to address a critical coverage gap.',
  monitoringPlanned: 'Checks have not reported yet for this area.',
};

export const FRAMEWORK_DISCLOSURE_COPY =
  'These controls are aligned to security best practices. This is not a compliance certification.';

export const OWASP_MAPPING_LABELS = {
  technicalHeading: 'Framework mapping (technical)',
  internalOnly: 'Aligned frameworks: OWASP Top 10 (mapping only)',
};

export type CoverageBadge = 'covered' | 'partial' | 'action' | 'planned';
export type CoverageBucket = 'covered' | 'managed' | 'needs_host' | 'action' | 'planned';

export const normalizeSecurityStatus = (input: {
  status?: string | null;
  severity?: string | null;
  enforced?: boolean | null;
  hasSignals?: boolean | null;
  enforcementAvailable?: boolean | null;
}): { badge: CoverageBadge; bucket: CoverageBucket; text: string; tone: 'good' | 'warn' | 'bad'; why?: string } => {
  const raw = `${input.status || ''}`.toLowerCase();
  const severity = `${input.severity || ''}`.toLowerCase();
  const hasSignals = input.hasSignals !== false;
  const enforcementAvailable = input.enforcementAvailable === true;

  if (!hasSignals || raw === 'planned' || raw === 'not_implemented' || raw === 'monitoring_planned') {
    return { badge: 'planned', bucket: 'planned', text: STATUS_LABELS.monitoringPlanned, tone: 'warn' };
  }

  if (raw === 'critical' || raw === 'high_risk' || severity === 'critical' || severity === 'high') {
    return { badge: 'action', bucket: 'action', text: STATUS_LABELS.actionRequired, tone: 'bad' };
  }

  if (raw === 'warn' || raw === 'degraded' || raw === 'partially_covered' || raw === 'partial' || severity === 'warn' || severity === 'medium') {
    return {
      badge: 'partial',
      bucket: 'needs_host',
      text: STATUS_LABELS.needsHost,
      tone: 'warn',
      why: STATUS_DESCRIPTIONS.needsHost,
    };
  }

  if (raw === 'ok' || raw === 'healthy' || raw === 'resolved' || raw === 'good' || input.enforced) {
    if (enforcementAvailable && !input.enforced) {
      return {
        badge: 'partial',
        bucket: 'managed',
        text: STATUS_LABELS.managed,
        tone: 'warn',
        why: STATUS_DESCRIPTIONS.managed,
      };
    }
    return { badge: 'covered', bucket: 'covered', text: STATUS_LABELS.covered, tone: 'good' };
  }

  return { badge: 'planned', bucket: 'planned', text: STATUS_LABELS.monitoringPlanned, tone: 'warn' };
};
